import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Inline agenda editing on the meeting detail page (Kloepfer 2026-07-31,
// reworked 2026-08-12). Larry: bring back "the original version where it
// kind of had the line items… you can move it with the grab bar up and
// down", plus sub-items under a topic. Edit mode is a ROW editor — drag
// handle, text input, indent/outdent, delete — never a raw textarea; the
// AI suggestions land as rows to review, not a text blob. Rows serialize
// back to the same one-line-per-item agenda text (two leading spaces =
// sub-item), so the server side is untouched.
export default class extends Controller {
  static values = {
    url: String, generateUrl: String, previousUrl: String, meetingId: Number,
    title: String, kind: String, items: Array,
    attachments: Object, presignUrl: String, registerUrl: String, removeUploadUrl: String,
    templatesUrl: String, templateId: String, templateName: String, previousTopUrl: String
  }
  static targets = ["list", "empty", "editor", "editBtn", "rows", "saveBtn",
    "generateBtn", "generateLabel", "fileInput",
    "prevModal", "prevSelect", "prevGo",
    "templateButtonLabel", "updateTemplateItem", "unlinkItem", "templatesModal", "templatesList", "templatesEmpty",
    "saveTemplateModal", "templateNameInput", "templateError", "saveTemplateBtn"]

  connect() {
    // [{text, child}] — the saved agenda, kept as the single source the
    // view list and a fresh editor both build from.
    this._items = (this.itemsValue || []).map(i => ({ text: i.text || "", band: !!i.band, level: Number(i.level ?? (i.child ? 1 : 0)) || 0 }))
    // Line text → [{id, filename, url}] (Larry 2026-08-20): files attached
    // to individual agenda lines. The server keeps this map in step with
    // renames and deletions on save.
    this._attachments = { ...(this.attachmentsValue || {}) }
    // Arriving from "Save & edit agenda" (2026-09-01): open the editor.
    if (new URLSearchParams(window.location.search).get("edit_agenda") === "1") {
      requestAnimationFrame(() => this.edit())
    }
  }

  disconnect() {
    this._teardownSortable()
  }

  edit() {
    // Already editing (AI / last-meeting rows sitting unsaved)? Edit must
    // never rebuild over them (Phelipe 2026-09-01) — just focus.
    if (!this.editorTarget.classList.contains("hidden")) {
      this.rowsTarget.querySelector("input")?.focus()
      return
    }
    this._buildRows(this._items)
    this._openEditor()
    const first = this.rowsTarget.querySelector("input")
    if (first) first.focus()
  }

  cancel() {
    this._closeEditor()
    this._refreshView(this._items)
  }

  // One button (Larry item 22 / Phelipe 2026-09-01): with a row focused,
  // Add item nests a NEW sub-item directly under it — one level deeper,
  // any depth. Nothing focused → a fresh topic at the bottom.
  addRow(event) {
    event?.preventDefault()
    this._snapshot()
    const active = this._activeRow
    if (active && active.isConnected && active.parentElement === this.rowsTarget) {
      const level = Math.min(this._rowLevel(active) + 1, 5)
      const row = this._rowEl({ text: "", level })
      if (active.nextSibling) this.rowsTarget.insertBefore(row, active.nextSibling)
      else this.rowsTarget.appendChild(row)
      row.querySelector("input").focus()
      this._refreshBandCounts()
    } else {
      // Hand-typed items get their own "Custom" section at the end
      // (Phelipe 2026-09-01, unconditional same day) — renameable, unlike
      // the imported bands. Reused while it's already the last section.
      const tailBand = [...this.rowsTarget.children].reverse().find(n => n.hasAttribute("data-agenda-band"))
      const inCustomTail = tailBand && this._bandIsCustom(tailBand.dataset.bandLabel || "")
      if (!inCustomTail) this.rowsTarget.appendChild(this._bandEl("Custom"))
      const row = this._rowEl({ text: "", level: 0 })
      this.rowsTarget.appendChild(row)
      row.querySelector("input").focus()
      this._refreshBandCounts()
    }
  }

  _rowLevel(row) { return Number(row?.dataset?.agendaLevel) || 0 }

  // ── Undo / redo (item 23, 2026-09-01): snapshots of the whole editor,
  // taken before each change — text edits snapshot when a field is first
  // focused, structural changes right before they land. ─────────────────
  _snapshot() {
    this._undoStack = this._undoStack || []
    const state = this._captureState()
    if (this._undoStack[this._undoStack.length - 1] === state) return
    this._undoStack.push(state)
    if (this._undoStack.length > 50) this._undoStack.shift()
    this._redoStack = []
    this._syncUndoButtons()
  }

  undo(event) {
    event?.preventDefault()
    if (!this._undoStack?.length) return
    this._redoStack = this._redoStack || []
    this._redoStack.push(this._captureState())
    this._restoreState(this._undoStack.pop())
    this._syncUndoButtons()
  }

  redo(event) {
    event?.preventDefault()
    if (!this._redoStack?.length) return
    this._undoStack = this._undoStack || []
    this._undoStack.push(this._captureState())
    this._restoreState(this._redoStack.pop())
    this._syncUndoButtons()
  }

  _syncUndoButtons() {
    const set = (sel, on) => {
      const btn = this.element.querySelector(sel)
      if (!btn) return
      btn.classList.toggle("opacity-60", !on)
      btn.classList.toggle("cursor-not-allowed", !on)
      btn.classList.toggle("cursor-pointer", on)
    }
    set("[data-agenda-undo]", !!this._undoStack?.length)
    set("[data-agenda-redo]", !!this._redoStack?.length)
  }

  _captureState() {
    const lines = []
    ;[...this.rowsTarget.children].forEach(node => {
      if (node.hasAttribute("data-agenda-band")) {
        const label = node.dataset.bandLabel?.trim()
        if (label) lines.push(`== ${label}`)
        return
      }
      if (!node.hasAttribute("data-agenda-row")) return
      const text = node.querySelector("input")?.value ?? ""
      lines.push("  ".repeat(this._rowLevel(node)) + text)
    })
    return lines.join("\n")
  }

  _restoreState(state) {
    this._buildRows(this._parseRaw(state.split("\n")))
  }

  _rowsHost() { return this.rowsTarget }

  rowFocused(event) {
    this._snapshot()
    this._activeRow = event.target.closest("[data-agenda-row]")
  }

  countsChanged() {
    clearTimeout(this._countTimer)
    this._countTimer = setTimeout(() => this._refreshBandCounts(), 250)
  }

  removeRow(event) {
    this._snapshot()
    event.currentTarget.closest("[data-agenda-row]")?.remove()
    this._refreshBandCounts()
  }

  // ‹ › move an item out or in a level (Larry: "a double arrow and you
  // could just keep moving them in") — capped one deeper than the row
  // above, so the outline can't skip levels.
  indentRow(event) {
    this._snapshot()
    const row = event.currentTarget.closest("[data-agenda-row]")
    if (!row) return
    const prev = row.previousElementSibling
    const cap = prev ? this._rowLevel(prev) + 1 : 0
    this._styleRow(row, Math.min(this._rowLevel(row) + 1, cap, 5))
  }

  outdentRow(event) {
    this._snapshot()
    const row = event.currentTarget.closest("[data-agenda-row]")
    if (!row) return
    this._styleRow(row, Math.max(this._rowLevel(row) - 1, 0))
  }

  // Enter adds the next row right below, keeping the same level — the
  // fastest way to type an agenda top to bottom.
  keydown(event) {
    if (event.key !== "Enter") return
    event.preventDefault()
    const row = event.target.closest("[data-agenda-row]")
    const next = this._rowEl({ text: "", level: this._rowLevel(row) })
    if (row?.nextSibling) this.rowsTarget.insertBefore(next, row.nextSibling)
    else this.rowsTarget.appendChild(next)
    next.querySelector("input").focus()
  }

  save() {
    if (this.hasSaveBtnTarget) this.saveBtnTarget.disabled = true
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content

    // Reworded lines carry their attachments along: saved-text → new-text.
    const renames = {}
    this.rowsTarget.querySelectorAll("[data-agenda-row]").forEach(row => {
      const orig = row.dataset.origKey
      const now = row.querySelector("input")?.value?.trim()
      if (orig && now && orig !== now) renames[orig] = now
    })

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrf
      },
      body: new URLSearchParams({ agenda: this._serializeRows(), renames: JSON.stringify(renames), agenda_template_id: this.templateIdValue || "" })
    })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d)))
      .then(data => {
        this._items = this._parseRaw(data.items || [])
        if (data.attachments) this._attachments = data.attachments
        this._closeEditor()
        this._refreshView(this._items)
        this._toast("Agenda updated.", "notice")
      })
      .catch(data => {
        this._toast((data && data.error) || "Could not save the agenda.", "alert")
      })
      .finally(() => {
        if (this.hasSaveBtnTarget) this.saveBtnTarget.disabled = false
      })
  }

  // AI suggestions (typical items for this meeting type + follow-ups from
  // the last meeting). They arrive as ROWS in the editor for review —
  // Larry 2026-08-11 on the textarea dump: "it's just really kind of
  // goofy… the other one gave you an item with grab bars".
  async generate() {
    if (this._generating || !this.hasGenerateUrlValue) return
    this._generating = true
    this._setGenerating(true)

    try {
      const resp = await fetch(this.generateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        credentials: "same-origin",
        body: JSON.stringify({
          title: this.titleValue || "",
          kind: this.kindValue || "",
          meeting_id: this.hasMeetingIdValue ? this.meetingIdValue : ""
        })
      })
      const data = await resp.json()
      const items = this._parseRaw(Array.isArray(data.items) ? data.items : [])
      if (!items.length) {
        this._toast("AI had nothing to add to this agenda.", "alert")
        return
      }
      // Append below whatever is already there so a hand-written agenda is
      // never wiped by a suggestion.
      if (this.editorTarget.classList.contains("hidden")) {
        this._buildRows(this._items)
        this._openEditor()
      }
      this._snapshot()
      this._pruneEmptyRows()
      this._appendBand("✨ AI suggestions — review, tweak, delete freely", "ai")
      items.forEach(item => this.rowsTarget.appendChild(this._rowEl(item)))
      this._refreshBandCounts()
    } catch (e) {
      this._toast("Could not draft the agenda.", "alert")
    } finally {
      this._generating = false
      this._setGenerating(false)
    }
  }

  // ── Editor internals ───────────────────────────────────────────────

  // ── Agenda templates (Larry item 20, 2026-09-01; Phelipe 2026-09-03) — the
  // same snapshot-and-link menu the meeting form has. Loading a template
  // opens the editor; nothing is saved to the meeting until Save.
  _templateHeaders() {
    const token = document.querySelector("meta[name='csrf-token']")?.content || ""
    return { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token }
  }

  _ensureEditing() {
    if (this.editorTarget.classList.contains("hidden")) {
      this._buildRows(this._items)
      this._openEditor()
    }
  }

  _setLinked(id, name) {
    this.templateIdValue = id ? String(id) : ""
    this.templateNameValue = name || ""
    if (this.hasTemplateButtonLabelTarget) this.templateButtonLabelTarget.textContent = id ? `Template: ${name}` : "Templates"
    if (this.hasUpdateTemplateItemTarget) this.updateTemplateItemTarget.classList.toggle("hidden", !id)
    if (this.hasUnlinkItemTarget) this.unlinkItemTarget.classList.toggle("hidden", !id)
  }

  stopModal(event) { event.stopPropagation() }
  closeTemplates() { this.templatesModalTarget.classList.add("hidden") }
  closeSaveTemplate() { this.saveTemplateModalTarget.classList.add("hidden") }

  async openTemplates() {
    this.templatesModalTarget.classList.remove("hidden")
    this.templatesListTarget.classList.remove("hidden")
    this.templatesListTarget.innerHTML = `<div class="px-3 py-3 text-[13px] text-gray-400">Loading…</div>`
    try {
      const resp = await fetch(this.templatesUrlValue, { headers: { "Accept": "application/json" } })
      const list = await resp.json()
      this.templatesListTarget.innerHTML = ""
      this.templatesEmptyTarget.classList.toggle("hidden", list.length > 0)
      this.templatesListTarget.classList.toggle("hidden", list.length === 0)
      list.forEach(t => {
        const row = document.createElement("div")
        row.className = "flex items-center gap-2 px-3 py-2"
        row.dataset.templateId = t.id
        row.innerHTML = `
          <div class="flex-1 min-w-0">
            <div data-name class="text-[13px] font-medium text-gray-800 truncate"></div>
            <div class="text-[13px] text-gray-400">${t.lines} line${t.lines === 1 ? "" : "s"} · updated ${t.updated}</div>
          </div>
          <button type="button" data-use class="px-2.5 py-1 text-[13px] font-medium text-white bg-brand hover:bg-brand-dark rounded-lg cursor-pointer">Use</button>
          ${t.can_edit ? `<button type="button" data-del class="p-1 text-gray-300 hover:text-red-600 cursor-pointer" title="Delete template">✕</button>` : ""}`
        row.querySelector("[data-name]").textContent = t.name
        row.querySelector("[data-use]").addEventListener("click", () => this.useTemplate(t.id, t.name))
        row.querySelector("[data-del]")?.addEventListener("click", () => this.deleteTemplate(t.id, t.name, row))
        this.templatesListTarget.appendChild(row)
      })
    } catch (_) {
      this.templatesListTarget.innerHTML = `<div class="px-3 py-3 text-[13px] text-red-600">Couldn't load templates.</div>`
    }
  }

  async useTemplate(id, name) {
    try {
      const resp = await fetch(`${this.templatesUrlValue}/${id}`, { headers: { "Accept": "application/json" } })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't load that template")
      this._ensureEditing()
      this._snapshot()
      this._restoreState(data.agenda || "")
      this._setLinked(id, name)
      this.closeTemplates()
      this._toast(`Agenda loaded from "${name}" — click Save to keep it`)
    } catch (err) {
      this._toast(err.message, "alert")
    }
  }

  openSaveTemplate() {
    this._ensureEditing()
    if (!this._captureState().trim()) return this._toast("Add something to the agenda first.", "alert")
    this.templateErrorTarget.classList.add("hidden")
    this.templateNameInputTarget.value = ""
    this.saveTemplateModalTarget.classList.remove("hidden")
    setTimeout(() => this.templateNameInputTarget.focus(), 50)
  }

  async saveTemplate() {
    const name = this.templateNameInputTarget.value.trim()
    if (!name) {
      this.templateErrorTarget.textContent = "Give the template a name."
      return this.templateErrorTarget.classList.remove("hidden")
    }
    this.saveTemplateBtnTarget.disabled = true
    try {
      const resp = await fetch(this.templatesUrlValue, { method: "POST", headers: this._templateHeaders(),
                                                        body: JSON.stringify({ name, agenda: this._captureState() }) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't save the template")
      this._setLinked(data.id, data.name)
      this.closeSaveTemplate()
      this._toast(`Saved as template "${data.name}"`)
    } catch (err) {
      this.templateErrorTarget.textContent = err.message
      this.templateErrorTarget.classList.remove("hidden")
    } finally {
      this.saveTemplateBtnTarget.disabled = false
    }
  }

  async updateTemplate() {
    const id = this.templateIdValue
    if (!id) return
    this._ensureEditing()
    try {
      const resp = await fetch(`${this.templatesUrlValue}/${id}`, { method: "PATCH", headers: this._templateHeaders(),
                                                                   body: JSON.stringify({ agenda: this._captureState() }) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't update the template")
      this._toast(`Template "${data.name}" updated`)
    } catch (err) {
      this._toast(err.message, "alert")
    }
  }

  unlinkTemplate() {
    this._setLinked(null, "")
    this._toast("Agenda unlinked from the template — click Save to keep it")
  }

  async deleteTemplate(id, name, row) {
    const btn = row.querySelector("[data-del]")
    if (btn && !btn.dataset.armed) {
      btn.dataset.armed = "1"; btn.textContent = "Delete?"; btn.className = "px-2 py-0.5 text-[13px] font-medium text-red-700 bg-red-50 rounded cursor-pointer"
      setTimeout(() => { if (btn.isConnected) { delete btn.dataset.armed; btn.textContent = "✕"; btn.className = "p-1 text-gray-300 hover:text-red-600 cursor-pointer" } }, 4000)
      return
    }
    try {
      const resp = await fetch(`${this.templatesUrlValue}/${id}`, { method: "DELETE", headers: this._templateHeaders() })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't delete the template")
      row.remove()
      if (String(this.templateIdValue) === String(id)) this._setLinked(null, "")
      if (!this.templatesListTarget.children.length) { this.templatesEmptyTarget.classList.remove("hidden"); this.templatesListTarget.classList.add("hidden") }
      this._toast(`Template "${name}" deleted`)
    } catch (err) {
      this._toast(err.message, "alert")
    }
  }

  _openEditor() {
    this.editorTarget.classList.remove("hidden")
    this.listTarget.classList.add("hidden")
    this.emptyTarget.classList.add("hidden")
    if (this.hasEditBtnTarget) this.editBtnTarget.classList.add("hidden")
    this._setupSortable()
  }

  _closeEditor() {
    this.editorTarget.classList.add("hidden")
    if (this.hasEditBtnTarget) this.editBtnTarget.classList.remove("hidden")
    this._teardownSortable()
  }

  _setupSortable() {
    if (this._sortable) return
    this._sortable = Sortable.create(this.rowsTarget, {
      handle: "[data-agenda-drag]",
      onStart: () => this._snapshot(),
      animation: 150,
      // Larry item 22 (2026-09-01): dropping a row in the last position.
      invertSwap: true,
      swapThreshold: 0.65,
      emptyInsertThreshold: 12
    })
  }

  _teardownSortable() {
    if (this._sortable) {
      this._sortable.destroy()
      this._sortable = null
    }
  }

  _buildRows(items) {
    this.rowsTarget.innerHTML = ""
    const filled = items.filter(i => (i.text || "").trim())
    const list = filled.length ? filled : [{ text: "", level: 0 }]
    list.forEach(item => {
      if (item.band) this.rowsTarget.appendChild(this._bandEl(item.text))
      else this.rowsTarget.appendChild(this._rowEl({ ...item, key: item.text }))
    })
    this._refreshBandCounts()
  }

  // Blank starter rows disappear the moment real items arrive (Phelipe
  // 2026-09-01: "remove the top empty one").
  _pruneEmptyRows() {
    this.rowsTarget.querySelectorAll("[data-agenda-row]").forEach(row => {
      const empty = !row.querySelector("input")?.value?.trim()
      const hasFiles = row.querySelector("[data-agenda-chips]")?.childElementCount > 0
      if (empty && !hasFiles) row.remove()
    })
  }

  // A review-time band above imported rows (Phelipe 2026-09-01: AI items
  // and last-meeting items read as their own sections, not the same
  // space). Not a data row — saving merges everything and bands vanish.
  _appendBand(label, _tone) {
    this.rowsTarget.appendChild(this._bandEl(label))
  }

  // Bands SAVE with the agenda ("== label" lines, Phelipe 2026-09-01) and
  // come back on every edit — removable with their ×. The AI and
  // from-meeting titles are fixed markers; CUSTOM bands (hand-added
  // sections) rename freely.
  // Rows belonging to a band: everything after it up to the next band.
  _bandSectionRows(band) {
    const rows = []
    let node = band.nextElementSibling
    while (node && !node.hasAttribute("data-agenda-band")) {
      if (node.hasAttribute("data-agenda-row")) rows.push(node)
      node = node.nextElementSibling
    }
    return rows
  }

  toggleBandCollapse(band) {
    const collapsed = band.dataset.bandCollapsed === "1"
    band.dataset.bandCollapsed = collapsed ? "0" : "1"
    band.querySelector("[data-band-chevron]")?.classList.toggle("-rotate-90", !collapsed)
    this._bandSectionRows(band).forEach(r => { r.style.display = collapsed ? "" : "none" })
  }

  // Deleting a divider takes its whole section (Phelipe 2026-09-01) —
  // styled confirm first when items would go with it.
  askDeleteBand(band) {
    const rows = this._bandSectionRows(band)
    const filled = rows.filter(r => r.querySelector("input")?.value?.trim()).length
    if (filled === 0) {
      this._snapshot()
      rows.forEach(r => r.remove())
      band.remove()
      this._refreshBandCounts()
      return
    }
    this._pendingBand = band
    const note = this._bandDeleteNoteEl()
    if (note) note.textContent = `"${band.dataset.bandLabel || "This section"}" and its ${filled} item${filled === 1 ? "" : "s"} are deleted together. This can't be undone here.`
    this._bandDeleteModalEl()?.classList.remove("hidden")
  }

  closeBandDelete() { this._bandDeleteModalEl()?.classList.add("hidden") }
  stopBandDelete(event) { event.stopPropagation() }

  confirmBandDelete() {
    this._snapshot()
    const band = this._pendingBand
    this.closeBandDelete()
    if (!band || !band.isConnected) return
    this._bandSectionRows(band).forEach(r => r.remove())
    band.remove()
    this._pendingBand = null
    this._refreshBandCounts()
  }

  _bandDeleteModalEl() { return this.element.querySelector("[data-band-delete-modal]") }
  _bandDeleteNoteEl() { return this.element.querySelector("[data-band-delete-note]") }

  _refreshBandCounts() {
    const host = this._rowsHost()
    host.querySelectorAll("[data-agenda-band]").forEach(band => {
      const n = this._bandSectionRows(band).filter(r => r.querySelector("input")?.value?.trim()).length
      const el = band.querySelector("[data-band-count]")
      if (el) el.textContent = `${n} item${n === 1 ? "" : "s"}`
      // Rows that arrived while collapsed stay in step.
      if (band.dataset.bandCollapsed === "1") this._bandSectionRows(band).forEach(r => { r.style.display = "none" })
    })
  }

  _bandIsCustom(label) {
    return !(/AI suggestions|From meeting/i.test(label) || label.startsWith("✨") || label.startsWith("↩"))
  }

  _bandEl(label) {
    const ai = /AI/i.test(label) || label.startsWith("✨")
    const custom = this._bandIsCustom(label)
    const band = document.createElement("div")
    band.setAttribute("data-agenda-band", "")
    band.dataset.bandLabel = label
    const tone = ai ? "bg-purple-50 border-purple-200 text-purple-700"
      : custom ? "bg-blue-50 border-blue-200 text-blue-700"
      : "bg-gray-100 border-gray-200 text-gray-600"
    band.className = `flex items-center gap-1.5 px-2.5 py-1 mt-3 rounded-lg border text-[13px] font-medium ${tone}`
    // Collapsible (Phelipe 2026-09-01): the chevron folds the section's
    // rows away; the count shows how many line items live inside.
    const chev = document.createElement("button")
    chev.type = "button"
    chev.title = "Collapse / expand this section"
    chev.className = "shrink-0 w-4 h-4 inline-flex items-center justify-center text-current opacity-60 hover:opacity-100 cursor-pointer transition-transform"
    chev.setAttribute("data-band-chevron", "")
    chev.innerHTML = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>`
    chev.addEventListener("click", () => this.toggleBandCollapse(band))
    band.append(chev)
    if (custom) {
      const input = document.createElement("input")
      input.type = "text"
      input.maxLength = 200
      input.value = label
      input.className = "flex-1 min-w-0 bg-transparent border-0 p-0 text-[13px] font-medium text-current focus:outline-none focus:ring-0"
      input.addEventListener("input", () => { band.dataset.bandLabel = input.value })
      band.append(input)
    } else {
      const span = document.createElement("span")
      span.className = "flex-1 truncate"
      span.textContent = label
      band.append(span)
    }
    const count = document.createElement("span")
    count.setAttribute("data-band-count", "")
    count.className = "shrink-0 text-[11px] font-normal opacity-70 tabular-nums"
    band.append(count)
    const x = document.createElement("button")
    x.type = "button"
    x.className = "text-current opacity-50 hover:opacity-100 cursor-pointer leading-none"
    x.title = "Remove this separator"
    x.textContent = "×"
    x.addEventListener("click", () => this.askDeleteBand(band))
    band.append(x)
    return band
  }

  _rowEl({ text, level, child, key }) {
    level = Number(level ?? (child ? 1 : 0)) || 0
    const row = document.createElement("div")
    row.setAttribute("data-agenda-row", "")
    // The line's SAVED text — attachments key off it; save() sends a rename
    // map when the text changed so the server moves them along.
    row.dataset.origKey = key || ""
    row.innerHTML = `
      <span data-agenda-drag class="px-0.5 mt-2 text-gray-300 hover:text-gray-500 cursor-grab shrink-0" title="Drag to reorder">
        <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor"><circle cx="2.5" cy="3" r="1.3"/><circle cx="7.5" cy="3" r="1.3"/><circle cx="2.5" cy="8" r="1.3"/><circle cx="7.5" cy="8" r="1.3"/><circle cx="2.5" cy="13" r="1.3"/><circle cx="7.5" cy="13" r="1.3"/></svg>
      </span>
      <button type="button" data-action="click->meeting-page-agenda#outdentRow"
              data-agenda-outdent title="Move out a level"
              class="w-4 h-5 mt-[7px] shrink-0 inline-flex items-center justify-center text-gray-300 hover:text-gray-600 cursor-pointer">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>
      </button>
      <button type="button" data-action="click->meeting-page-agenda#indentRow"
              data-agenda-indent title="Move in a level (sub-item)"
              class="w-4 h-5 mt-[7px] shrink-0 inline-flex items-center justify-center text-gray-300 hover:text-gray-600 cursor-pointer">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7"/></svg>
      </button>
      <div class="flex-1 min-w-0">
        <input type="text" value="" maxlength="500" placeholder="Agenda item"
               data-action="keydown->meeting-page-agenda#keydown focusin->meeting-page-agenda#rowFocused paste->meeting-page-agenda#rowPaste input->meeting-page-agenda#countsChanged"
               class="w-full px-2.5 py-1.5 text-[13px] border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">
      </div>
      <div data-agenda-zone data-action="click->meeting-page-agenda#rowZoneClick dragover->meeting-page-agenda#rowZoneDragOver dragenter->meeting-page-agenda#rowZoneDragOver dragleave->meeting-page-agenda#rowZoneDragLeave drop->meeting-page-agenda#rowZoneDrop"
           class="flex w-96 shrink-0 items-center px-2 py-1 min-h-[34px] border border-dashed border-amber-200 bg-amber-50 rounded-lg text-[13px] text-amber-400 hover:text-amber-600 hover:border-amber-300 cursor-pointer transition-colors select-none"
           title="Attach to this line">
        <span data-agenda-zone-hint class="w-full text-center">Drop, paste, or click to attach</span>
        <div data-agenda-chips class="hidden flex-wrap items-center gap-1"></div>
      </div>
      <button type="button" data-action="click->meeting-page-agenda#agendaAttach" data-agenda-clip
              class="w-5 h-5 mt-[7px] shrink-0 inline-flex items-center justify-center text-gray-300 hover:text-brand cursor-pointer" title="Attach a file">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01"/></svg>
      </button>
      <button type="button" data-action="click->meeting-page-agenda#removeRow"
              class="w-5 h-5 mt-[7px] shrink-0 text-gray-300 hover:text-red-600 cursor-pointer leading-none text-base" title="Remove">×</button>
    `
    row.querySelector("input").value = text
    this._styleRow(row, level)
    this._renderRowChips(row)
    return row
  }

  _styleRow(row, level) {
    level = Math.max(0, Math.min(Number(level) || 0, 5))
    row.dataset.agendaLevel = String(level)
    row.dataset.agendaChildRow = level > 0 ? "true" : "false"
    row.className = "flex items-start gap-1.5"
    row.style.marginLeft = level ? `${level * 1.75}rem` : ""
    const indentBtn = row.querySelector("[data-agenda-indent]")
    if (indentBtn) indentBtn.classList.toggle("text-brand", level > 0)
    const input = row.querySelector("input")
    if (input) {
      input.classList.toggle("text-gray-600", level > 0)
      // Main topics read bold in the editor, like the saved list.
      input.classList.toggle("font-semibold", level === 0)
    }
  }

  // ── From last meeting (item 20, 2026-09-01): the newest same-type
  // meeting's agenda, or any earlier date of that type from the dropdown.
  // Items append as editor rows to review — never a silent overwrite. ───
  async openPrevious() {
    if (!this.hasPreviousUrlValue) return
    try {
      const resp = await fetch(this.previousUrlValue, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      const data = await resp.json()
      const meetings = data.meetings || []
      if (!meetings.length) {
        this._toast("No earlier meeting of this type has an agenda yet.", "alert")
        return
      }
      this._prevMeetings = meetings
      this.prevSelectTarget.innerHTML = meetings.map((m, i) =>
        `<option value="${m.id}"${i === 0 ? " selected" : ""}>${this._esc(m.label)}${i === 0 ? " — last meeting" : ""}</option>`).join("")
      this.prevModalTarget.classList.remove("hidden")
    } catch {
      this._toast("Couldn't load past meetings.", "alert")
    }
  }

  closePrevious() { this.prevModalTarget.classList.add("hidden") }
  stopPrevious(event) { event.stopPropagation() }

  // "From last meeting" (Phelipe 2026-09-06): the AI's top 5 from the picked
  // meeting, not its whole agenda. Lands in the editor like the AI does.
  async confirmPrevious() {
    const id = Number(this.prevSelectTarget.value)
    const picked = (this._prevMeetings || []).find(m => m.id === id)
    this.closePrevious()
    if (!picked) return
    this._toast("Picking the 5 most important items…")
    try {
      const resp = await fetch(this.previousTopUrlValue, {
        method: "POST", headers: this._templateHeaders(),
        body: JSON.stringify({ meeting_id: picked.id, title: this.titleValue, kind: this.kindValue })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't read that meeting")
      const items = (data.items || []).filter(t => t && t.trim())
      if (!items.length) return this._toast("That meeting has nothing to carry forward.", "alert")
      this._ensureEditing()
      this._snapshot()
      this._pruneEmptyRows()
      this._appendBand(`↩ From meeting ${data.label || picked.label} — top ${items.length}`, "prev")
      items.forEach(text => this.rowsTarget.appendChild(this._rowEl({ text, level: 0 })))
      this._syncUndoButtons()
    } catch (err) {
      this._toast(err.message, "alert")
    }
  }

  // ── Agenda line attachments (Larry 2026-08-20) ────────────────────

  agendaAttach(event) {
    const row = event.currentTarget.closest("[data-agenda-row]")
    if (!row) return
    const text = row.querySelector("input")?.value?.trim()
    if (!text) {
      this._toast("Type the agenda item first.", "alert")
      return
    }
    this._uploadRow = row
    this.fileInputTarget.click()
  }

  async agendaFilesChosen() {
    const files = [...this.fileInputTarget.files]
    this.fileInputTarget.value = ""
    await this._uploadToRow(this._uploadRow, files)
  }

  // ── Per-line drop zone (Larry item 24, built 2026-09-01): the empty
  // stretch right of each line takes files by drag, paste, or click. ────
  rowZoneClick(event) {
    if (event.target.closest("a, button")) return
    this.agendaAttach(event)
  }

  rowZoneDragOver(event) {
    event.preventDefault()
    event.currentTarget.classList.add("border-amber-400", "bg-amber-100", "text-amber-700")
  }

  rowZoneDragLeave(event) {
    event.currentTarget.classList.remove("border-amber-400", "bg-amber-100", "text-amber-700")
  }

  rowZoneDrop(event) {
    event.preventDefault()
    this.rowZoneDragLeave(event)
    const files = [...(event.dataTransfer?.files || [])]
    this._uploadToRow(event.currentTarget.closest("[data-agenda-row]"), files)
  }

  // Paste works on the line's text box too — a screenshot on the clipboard
  // lands as that line's file (Larry: grab a piece of the plan in Bluebeam,
  // copy, paste it on the item).
  rowPaste(event) {
    const files = [...(event.clipboardData?.files || [])]
    if (!files.length) return
    event.preventDefault()
    this._uploadToRow(event.target.closest("[data-agenda-row]"), files)
  }

  async _uploadToRow(row, files) {
    if (!files.length || !row || !row.isConnected) return
    if (!row.querySelector("input")?.value?.trim()) {
      this._toast("Type the agenda item first.", "alert")
      return
    }
    const key = row.dataset.origKey || row.querySelector("input")?.value?.trim()
    if (!key) return
    // A brand-new line keys by what's typed right now; a later rewording
    // rides the save()'s rename map.
    if (!row.dataset.origKey) row.dataset.origKey = key

    const clip = row.querySelector("[data-agenda-clip]")
    clip?.classList.add("animate-pulse")
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const headers = { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrf }
      const presign = await fetch(this.presignUrlValue, {
        method: "POST", headers, credentials: "same-origin",
        body: JSON.stringify({ files: files.map(f => ({ filename: f.name, content_type: f.type || "application/octet-stream", byte_size: f.size })) })
      })
      const presigned = await presign.json()
      if (!presign.ok || !presigned.uploads) throw new Error(presigned.error || "Could not attach the file.")
      await Promise.all(presigned.uploads.map((u, i) =>
        fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: files[i] })
          .then(r => { if (!r.ok) throw new Error("Could not attach the file.") })
      ))
      const reg = await fetch(this.registerUrlValue, {
        method: "POST", headers, credentials: "same-origin",
        body: JSON.stringify({ line_key: key, files: presigned.uploads.map(u => ({ cache_name: u.cache_name, filename: u.filename })) })
      })
      const data = await reg.json()
      if (!reg.ok || !data.ok) throw new Error(data.error || "Could not attach the file.")
      this._attachments = data.attachments || {}
      this._renderRowChips(row)
    } catch (e) {
      this._toast(e.message || "Could not attach the file.", "alert")
    } finally {
      clip?.classList.remove("animate-pulse")
    }
  }

  async removeAgendaAttachment(event) {
    event.preventDefault()
    event.stopPropagation()
    const btn = event.currentTarget
    const row = btn.closest("[data-agenda-row]")
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(`${this.removeUploadUrlValue}?attachment_id=${btn.dataset.attachmentId}`, {
        method: "DELETE",
        headers: { "Accept": "application/json", "X-CSRF-Token": csrf },
        credentials: "same-origin"
      })
      const data = await resp.json()
      if (!resp.ok || !data.ok) throw new Error()
      this._attachments = data.attachments || {}
      if (row) this._renderRowChips(row)
    } catch {
      this._toast("Could not remove the file.", "alert")
    }
  }

  // Chips under an editor row: the line's attached files, each removable.
  _renderRowChips(row) {
    const box = row.querySelector("[data-agenda-chips]")
    if (!box) return
    const key = row.dataset.origKey || row.querySelector("input")?.value?.trim()
    const list = (key && this._attachments?.[key]) || []
    box.innerHTML = list.map(a => `
      <span class="inline-flex items-center gap-1 px-1.5 py-px rounded bg-gray-100 text-[13px] text-gray-600 max-w-[170px]">
        <a href="${a.url}" target="_blank" rel="noopener" class="truncate hover:text-gray-800">${this._esc(a.filename)}</a>
        <button type="button" data-attachment-id="${a.id}" data-action="click->meeting-page-agenda#removeAgendaAttachment"
                class="text-gray-400 hover:text-red-600 cursor-pointer leading-none" title="Remove file">×</button>
      </span>`).join("")
    box.classList.toggle("hidden", !list.length)
    box.classList.toggle("flex", !!list.length)
    // Files replace the hint in the right column (Phelipe 2026-09-01);
    // the whole zone stays a drop/paste/click target either way.
    const hint = row.querySelector("[data-agenda-zone-hint]")
    if (hint) hint.classList.toggle("hidden", !!list.length)
    const clip = row.querySelector("[data-agenda-clip]")
    if (clip) {
      clip.classList.toggle("text-brand", !!list.length)
      clip.classList.toggle("text-gray-300", !list.length)
    }
  }

  _esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, c => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }

  _serializeRows() {
    const lines = []
    ;[...this.rowsTarget.children].forEach(node => {
      if (node.hasAttribute("data-agenda-band")) {
        const label = node.dataset.bandLabel?.trim()
        if (label) lines.push(`== ${label}`)
        return
      }
      if (!node.hasAttribute("data-agenda-row")) return
      const text = node.querySelector("input")?.value?.trim()
      if (!text) return
      lines.push("  ".repeat(this._rowLevel(node)) + text)
    })
    return lines.join("\n")
  }

  // Raw agenda lines (two leading spaces / a tab = sub-item) → row objects.
  _parseRaw(lines) {
    return lines.map(raw => {
      if (raw.trim().startsWith("== ")) {
        const label = raw.trim().slice(3).trim()
        return label ? { text: label, band: true, level: 0 } : null
      }
      const tabs = (raw.match(/^\t+/) || [""])[0].length
      const spaces = (raw.match(/^ +/) || [""])[0].length
      const level = Math.min(tabs > 0 ? tabs : Math.floor(spaces / 2), 5)
      const text = raw.trim().replace(/^[-•*]\s+/, "")
      return text ? { text, level } : null
    }).filter(Boolean)
  }

  _setGenerating(on) {
    if (this.hasGenerateLabelTarget) {
      this.generateLabelTarget.textContent = on ? "Drafting…" : "Suggest with AI"
    }
    if (this.hasGenerateBtnTarget) {
      this.generateBtnTarget.classList.toggle("opacity-60", on)
      this.generateBtnTarget.classList.toggle("pointer-events-none", on)
    }
  }

  // Rebuild the numbered view list — same rule as the server render
  // (MeetingsHelper#parse_agenda_items): topics count up, sub-items dot.
  _refreshView(items) {
    this.listTarget.innerHTML = ""
    let topicNumber = 0
    items.forEach(({ text, level, child, band }) => {
      if (!text) return
      if (band) {
        const li = document.createElement("li")
        li.className = "pt-2 text-[13px] font-medium " + (/AI/i.test(text) || text.startsWith("✨") ? "text-purple-600" : "text-gray-400")
        li.textContent = text
        this.listTarget.appendChild(li)
        return
      }
      level = Number(level ?? (child ? 1 : 0)) || 0
      child = level > 0
      if (!child) topicNumber += 1
      const li = document.createElement("li")
      li.className = `flex items-start gap-2.5 text-sm ${child ? "text-gray-600" : "text-gray-800"}`
      li.style.paddingLeft = child ? `${level * 1.75}rem` : ""
      const marker = document.createElement("span")
      if (child) {
        marker.className = "mt-[7px] rounded-full shrink-0 w-1 h-1 bg-gray-300"
      } else {
        marker.className = "shrink-0 w-5 text-right tabular-nums text-gray-400 font-medium"
        marker.textContent = `${topicNumber}.`
      }
      const span = document.createElement("span")
      if (!child) span.className = "font-medium"
      span.textContent = text
      const wrap = document.createElement("span")
      wrap.className = "min-w-0"
      wrap.appendChild(span)
      // Paperclip chips inline after the line (Larry 2026-08-20) — mirrors
      // the server-rendered list.
      ;(this._attachments?.[text] || []).forEach(a => {
        const chip = document.createElement("a")
        chip.href = a.url
        chip.target = "_blank"
        chip.rel = "noopener"
        chip.className = "inline-flex items-center gap-1 align-middle ml-2 px-1.5 py-px rounded bg-gray-100 hover:bg-gray-200 text-[13px] text-gray-600 max-w-[220px]"
        chip.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="shrink-0"><path d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01"/></svg>`
        const nameEl = document.createElement("span")
        nameEl.className = "truncate"
        nameEl.textContent = a.filename
        chip.appendChild(nameEl)
        wrap.appendChild(chip)
      })
      li.appendChild(marker)
      li.appendChild(wrap)
      this.listTarget.appendChild(li)
    })
    this.listTarget.classList.toggle("hidden", items.length === 0)
    this.emptyTarget.classList.toggle("hidden", items.length > 0)
  }

  // type defaults to "notice": callers like updateTemplate pass none, and
  // "flash-toast-undefined" rendered nothing (Larry 2026-09-08: "it's not
  // acting like anything's happening").
  _toast(message, type = "notice") {
    const flash = document.createElement("div")
    flash.className = `flash-toast-${type}`
    flash.setAttribute("data-controller", "flash")
    flash.setAttribute("data-flash-dismiss-after-value", "5000")
    flash.setAttribute("role", "alert")
    flash.innerHTML = `<span>${message}</span>`
    document.body.appendChild(flash)
  }
}
